import React, { useRef, useState } from 'react';
import { AlertTriangleIcon, CameraIcon, CheckCircle2Icon, EyeIcon, FileUpIcon, ImageUpIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { CaseDocument } from '../../types/case';
import type { CitizenScreen } from '../../types/navigation';
import type { CitizenSession } from './CitizenApp';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { DocPreview } from '../../components/shared/DocPreview';
import { Modal } from '../../components/shared/Modal';
import { DOC_LABEL } from '../../utils/caseStatus';
import { bn, fmtDateTime, nowIso } from '../../utils/bn';
export function DocumentUpload({
  pcr,
  session,
  go
}: {
  pcr: string;
  session: CitizenSession;
  go: (s: CitizenScreen) => void;
}) {
  const {
    getCase,
    updateCase
  } = useStore();
  const c = getCase(pcr);
  const fileRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<{
    key: string;
    type: 'image' | 'pdf';
  } | null>(null);
  const [preview, setPreview] = useState<CaseDocument | null>(null);
  if (!c) return <p className="p-6" data-id="element-968" data-mp="u5jrdgikbvz">কেস পাওয়া যায়নি।</p>;
  const locked = c.docStatus === 'submitted' || c.docStatus === 'verifying' || c.docStatus === 'verified' || c.stage === 'released';
  const uploaded = c.documents.filter(d => d.fileName && d.status !== 'correction').length;
  const ready = c.documents.every(d => !d.required || d.fileName && d.status !== 'correction');
  const attach = (key: string, fileName: string, fileType: 'image' | 'pdf') => {
    updateCase(pcr, x => ({
      ...x,
      documents: x.documents.map(d => d.key === key ? {
        ...d,
        fileName,
        fileType,
        status: 'submitted',
        source: 'online',
        uploadedAt: nowIso(),
        reason: undefined
      } : d)
    }));
    toast.success('ফাইল সংযুক্ত হয়েছে।');
  };
  const pick = (key: string, type: 'image' | 'pdf') => {
    setTarget({
      key,
      type
    });
    if (fileRef.current) {
      fileRef.current.accept = type === 'pdf' ? 'application/pdf' : 'image/*';
      fileRef.current.click();
    }
  };
  const submit = () => {
    if (!ready) {
      toast.error('সকল আবশ্যক কাগজপত্র সংযুক্ত করুন।');
      return;
    }
    updateCase(pcr, x => ({
      ...x,
      docStatus: 'submitted',
      ownerLinked: true,
      documents: x.documents.map(d => d.status === 'submitted' ? {
        ...d,
        status: 'verifying'
      } : d)
    }), {
      key: 'docs_submitted',
      actor: session.name,
      role: 'যানবাহন মালিক',
      action: 'অনলাইনে কাগজপত্র যাচাইয়ের জন্য জমা'
    });
    toast.success('কাগজপত্র যাচাইয়ের জন্য জমা হয়েছে। বিভাগীয় কর্মকর্তা পর্যালোচনা করবেন।');
    go({
      name: 'case',
      pcr
    });
  };
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন সেবা" title="প্রয়োজনীয় কাগজপত্র জমা" onBack={() => go({
      name: 'case',
      pcr
    })} data-id="element-969" data-mp="1h7e5zg8b6ap5" />
      <input ref={fileRef} type="file" className="hidden" onChange={e => {
      const f = e.target.files?.[0];
      if (f && target) attach(target.key, f.name, target.type);
      e.target.value = '';
    }} data-id="element-970" data-mp="1m5o0rpq4f9a8" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-971" data-mp="1wbpa05e6i3ab">
        <div className="card p-3" data-id="element-972" data-mp="1wdschowipovx">
          <div className="flex items-center justify-between text-sm" data-id="element-973" data-mp="1rc2wduuful0z">
            <span className="font-semibold text-ink" data-id="element-974" data-mp="1refnaxi6c5q6">{c.pcr}</span>
            <span className="text-ink-muted" data-id="element-975" data-mp="1nclpqy39zhu8">{bn(uploaded)}/{bn(c.documents.length)} সংযুক্ত</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-canvas" data-id="element-976" data-mp="2k0i8a8i9wn83"><div className="h-2 rounded-full bg-bdgreen transition-[width] duration-300" style={{
            width: `${uploaded / Math.max(1, c.documents.length) * 100}%`
          }} data-id="element-977" data-mp="2f1oxinudfabm" /></div>
          {locked && <p className="mt-2 text-xs text-navy" data-id="element-978" data-mp="269vemurddniv">কাগজপত্র {DOC_LABEL[c.docStatus].label} — এই মুহূর্তে পরিবর্তন করা যাবে না।</p>}
        </div>

        {c.documents.map(d => {
        const st = DOC_LABEL[d.status];
        const canEdit = !locked || d.status === 'correction';
        return <div key={d.key} className={`card p-3 ${d.status === 'correction' ? 'border-danger/40' : ''}`} data-id="element-979" data-mp="2ijb790ewmycg">
              <div className="flex items-start justify-between gap-2" data-id="element-980" data-mp="2ninw1qpc6fho">
                <div data-id="element-981" data-mp="2nfrzo6t4zyvc">
                  <p className="text-sm font-bold text-ink" data-id="element-982" data-mp="29iv7a0xde9cc">{d.name}{d.required && <span className="text-danger" data-id="element-983" data-mp="29kg5q1mt2cfh"> *</span>}</p>
                  {d.uploadedAt && <p className="text-[11px] text-ink-soft" data-id="element-984" data-mp="2dkgq02wl5hug">{d.source === 'office' ? 'সরাসরি অফিসে জমা' : 'অনলাইনে জমা'} · {fmtDateTime(d.uploadedAt)}</p>}
                </div>
                <StatusBadge label={st.label} tone={st.tone} size="sm" data-id="element-985" data-mp="367korq534zxs" />
              </div>
              {d.status === 'correction' && d.reason && <p className="mt-2 flex items-start gap-1.5 rounded-md bg-danger-50 p-2 text-xs text-danger-700" data-id="element-986" data-mp="2sfejx07dufdn"><AlertTriangleIcon className="h-3.5 w-3.5 shrink-0 mt-px" data-id="element-987" data-mp="2wggaytr5bl4j" /> {d.reason}</p>}
              {d.fileName && <button onClick={() => setPreview(d)} className="mt-2 block w-full text-left" data-id="element-988" data-mp="3ozdixnvw4r73">
                  <DocPreview name={d.name} ownerName={c.ownerName} regNo={c.regNo} fileName={d.fileName} fileType={d.fileType} data-id="element-989" data-mp="1djd0pqse8lkc" />
                </button>}
              {canEdit && <div className="mt-2 grid grid-cols-3 gap-1.5" data-id="element-990" data-mp="1na7nqxdgrp6u">
                  <button className="btn btn-sm btn-outline" onClick={() => pick(d.key, 'image')} data-id="element-991" data-mp="1nbshwlvv9646"><ImageUpIcon className="h-3.5 w-3.5" data-id="element-992" data-mp="1ibmwhng6wfuj" /> ছবি</button>
                  <button className="btn btn-sm btn-outline" onClick={() => pick(d.key, 'pdf')} data-id="element-993" data-mp="umbslx3jm5c6"><FileUpIcon className="h-3.5 w-3.5" data-id="element-994" data-mp="pmpfdnnpsp3d" /> PDF</button>
                  <button className="btn btn-sm btn-outline" onClick={() => attach(d.key, `camera_${d.key}_${Date.now() % 10000}.jpg`, 'image')} data-id="element-995" data-mp="pok47d23x1cq"><CameraIcon className="h-3.5 w-3.5" data-id="element-996" data-mp="zju680si98qi" /> ক্যামেরা</button>
                </div>}
              {d.fileName && canEdit && <p className="mt-1.5 text-[11px] text-ink-soft" data-id="element-997" data-mp="2fxan1rpys3y5">নতুন ফাইল নির্বাচন করলে বর্তমান ফাইল প্রতিস্থাপিত হবে।</p>}
              {d.status === 'verified' && <p className="mt-2 flex items-center gap-1 text-xs text-success" data-id="element-998" data-mp="2i2qmr72a5frj"><CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-999" data-mp="2i2gzhr8mdmpj" /> কর্মকর্তা কর্তৃক যাচাইকৃত</p>}
            </div>;
      })}
      </div>
      {!locked || c.docStatus === 'correction' ? <div className="shrink-0 border-t border-line bg-white p-3" data-id="element-1000" data-mp="2s1fw2bg41rw6">
          <button className="btn btn-green btn-lg w-full" disabled={!ready} onClick={submit} data-id="element-1001" data-mp="2x09d2dulw113">যাচাইয়ের জন্য জমা দিন</button>
        </div> : null}

      <Modal open={!!preview} onClose={() => setPreview(null)} title="ফাইল প্রিভিউ" contained footer={<button className="btn btn-outline" onClick={() => setPreview(null)} data-id="element-1003" data-mp="248zctxkmztva"><EyeIcon className="h-4 w-4" data-id="element-1004" data-mp="298u6g8vsqxk1" /> বন্ধ করুন</button>} data-id="element-1002" data-mp="1zaeczz9arc7d">
        {preview && <DocPreview large name={preview.name} ownerName={c.ownerName} regNo={c.regNo} fileName={preview.fileName} fileType={preview.fileType} data-id="element-1005" data-mp="2e1dg4urd58jk" />}
      </Modal>
    </>;
}